import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getCookie } from './getCookie';

export const API_BASE_URLS = {
  workflow: import.meta.env.VITE_AI_BASE_WORKFLOW_URL || 'https://bprm-ai-api.workfinity.com/api/workflow-service',
  auth: import.meta.env.VITE_API_BASE_URL_BPRM || 'https://bprm-ai-api.workfinity.com/api',
  code: import.meta.env.VITE_API_BASE_URL_CODEMANAGER_BPRM || 'https://bprm-ai-api.workfinity.com/api/codemanager-service/api',
} as const;

const LOGIN_URL = 'https://bprm-ai-portal.workfinity.com/login';



export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  access_token?: string;
  token_type?: string;
}

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  client_id: string;
}

// Axios instance creator
function createAxiosInstance(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: 120000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add CSRF token to every request
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const csrfToken = getCookie('csrf_token');
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
      // config.headers['x-internal-auth'] = "wf_internal_7uFq3sV8k2Qp1mN6yX0dJr9vL5aZcT4hG8nP2sW5";

      const appId = getCookie("app_id") || "1";
      config.headers["X-App-Id"] = appId;
      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
}

// Create instances for different base URLs
export const apiWorkflow = createAxiosInstance(API_BASE_URLS.workflow);
export const authClient = createAxiosInstance(API_BASE_URLS.auth);
export const codeManagerClient = createAxiosInstance(API_BASE_URLS.code);

// Token refresh state management
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Redirect to login
export const redirectToLogin = () => {
  window.location.href = LOGIN_URL;
};

// Refresh token function
export const refreshToken = async (): Promise<boolean> => {
  try {
    const response = await authClient.post<RefreshTokenResponse>('/auth/refresh');
    return response.data.success;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

// Handle logout
export const handleLogout = async () => {
  // try {
  //   await authClient.post('/auth/logout');
  // } catch (error) {
  //   console.error('Logout failed:', error);
  // } finally {
  //   redirectToLogin();
  // }
};

// Response interceptor - Handle 401/403 errors with token refresh
const setupResponseInterceptor = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Check if error is 401 or 403 and we haven't retried yet
      if (
        (error.response?.status === 401 || error.response?.status === 403) &&
        originalRequest &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          // Wait for token refresh to complete
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token: string) => {
              if (originalRequest.headers) {
                resolve(client(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshSuccess = await refreshToken();

          if (refreshSuccess) {
            isRefreshing = false;
            onRefreshed('success');
            return client(originalRequest);
          } else {
            isRefreshing = false;
            await handleLogout();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          isRefreshing = false;
          await handleLogout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

// Apply interceptors to all clients
setupResponseInterceptor(apiWorkflow);
setupResponseInterceptor(authClient);
setupResponseInterceptor(codeManagerClient);

// Utility function to handle API errors
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || 'An error occurred';
  }
  return error instanceof Error ? error.message : 'An unknown error occurred';
};